import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Menu } from "lucide-react";
export default function ServingSince2005() {
  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100", "translate-y-0");
          entry.target.classList.remove("opacity-0", "translate-y-8");
        }
      });
    }, {
      threshold: 0.15
    });
    [textRef, imageRef, statsRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} className="bg-background text-foreground overflow-hidden relative">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500&display=swap');`}</style>

      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-700 via-amber-500 to-amber-700" />

      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">

        <div ref={textRef} className="opacity-0 translate-y-8 transition-all duration-700 ease-out mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-amber-200" />
            <Badge className="uppercase tracking-[0.25em] text-xs font-medium px-4 py-1.5 rounded-none border border-amber-700 text-amber-800 bg-amber-50" style={{
            fontFamily: "'DM Sans', sans-serif"
          }}>
              Est. 2005
            </Badge>
            <div className="h-px flex-1 bg-amber-200" />
          </div>

          <h2 className="text-6xl md:text-8xl lg:text-9xl font-black leading-none tracking-tight text-center text-stone-900" style={{
          fontFamily: "'Playfair Display', serif"
        }}>
            Serving
            <span className="block italic text-amber-700">Since 2005</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">

          <div ref={imageRef} className="opacity-0 translate-y-8 transition-all duration-700 delay-200 ease-out lg:col-span-7 relative">
            <div className="absolute inset-0 bg-amber-900 translate-x-3 translate-y-3 -z-10 rounded-none" />
            <div className="overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/985960/img/chips-tostadas-1.jpg" className="w-full h-auto object-cover block" style={{
              maxHeight: "520px",
              objectPosition: "center"
            }} alt="chips-tostadas" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-900/60 to-transparent" />
            <div className="absolute bottom-6 left-6" style={{
            fontFamily: "'DM Sans', sans-serif"
          }}>
              <span className="text-white/90 text-sm uppercase tracking-widest font-medium">
                Authentic Mexico Food
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between bg-stone-900 text-stone-100 p-8 md:p-12">

            <div className="opacity-0 translate-y-8 transition-all duration-700 delay-300 ease-out" ref={statsRef}>
              <div className="grid grid-cols-3 gap-4 mb-10">
                {[{
                num: "20+",
                label: "Years Serving"
              }, {
                num: "100%",
                label: "Authentic"
              }, {
                num: "Fresh",
                label: "Every Day"
              }].map((stat, i) => <div key={i} className="text-center">
                    <div className="text-3xl font-black text-amber-400 leading-none mb-1" style={{
                  fontFamily: "'Playfair Display', serif"
                }}>
                      {stat.num}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-stone-400" style={{
                  fontFamily: "'DM Sans', sans-serif"
                }}>
                      {stat.label}
                    </div>
                  </div>)}
              </div>

              <Separator className="bg-stone-700 mb-8" />

              <p className="text-stone-300 leading-relaxed text-base font-light" style={{
              fontFamily: "'DM Sans', sans-serif"
            }}>
                Welcome to our small business, where we have been crafting
                delicious Mexican tortillas since 2005. We are dedicated to
                providing our customers with the most authentic and high-quality
                tortillas possible. Our tortillas are made using traditional
                techniques and the finest ingredients, resulting in a product
                that is both tasty and nutritious.
              </p>

              <p className="text-stone-400 leading-relaxed text-base font-light mt-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }}>
                We offer a variety of tortilla flavors and sizes, including
                gluten-free and vegan options. Our commitment to quality and
                customer satisfaction is what sets us apart from the rest. We
                are proud to have served our community for over 15 years and
                look forward to continuing to do so for many more.
              </p>

              <Separator className="bg-stone-700 my-8" />

              <div className="flex flex-wrap gap-2">
                {["Barbacoa", "Carnitas", "Chicharron", "Birria", "Gluten-Free", "Vegan Options"].map(tag => <span key={tag} className="px-3 py-1 text-xs uppercase tracking-wider border border-stone-600 text-stone-400 hover:border-amber-500 hover:text-amber-400 transition-colors duration-200 cursor-default" style={{
                fontFamily: "'DM Sans', sans-serif"
              }}>
                    {tag}
                  </span>)}
              </div>
            </div>

            <div className="mt-10">
              <a href="#menu" className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white uppercase tracking-[0.2em] text-sm font-medium px-8 py-4 transition-colors duration-200 group" style={{
              fontFamily: "'DM Sans', sans-serif"
            }}>
                View Menu
                <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
                  &rarr;
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}